import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery } from "@tanstack/react-query";
import { getPublishedPost } from "@/lib/blog.functions";

export const Route = createFileRoute("/blog/$slug")({
  head: ({ params }) => ({
    meta: [
      { title: `${params.slug} — thatKorner Blog` },
      { property: "og:url", content: `https://www.thatkorner.com/blog/${params.slug}` },
    ],
    links: [{ rel: "canonical", href: `https://www.thatkorner.com/blog/${params.slug}` }],
  }),
  component: PostPage,
  notFoundComponent: () => (
    <div className="bg-ink py-32 text-center text-bone">
      <p className="font-script text-3xl text-ember">missing</p>
      <h1 className="h-display mt-2 text-5xl">Post not found</h1>
      <Link to="/blog" className="mt-6 inline-block text-ember underline">
        Back to blog
      </Link>
    </div>
  ),
  errorComponent: ({ error }) => (
    <div className="bg-ink py-32 text-center text-bone">
      <h1 className="h-display text-3xl">Something went wrong</h1>
      <p className="mt-3 text-bone/60">{error.message}</p>
    </div>
  ),
});

function PostPage() {
  const { slug } = Route.useParams();
  const fetchPost = useServerFn(getPublishedPost);
  const { data, isLoading } = useQuery({
    queryKey: ["blog", slug],
    queryFn: () => fetchPost({ data: { slug } }),
  });

  if (isLoading) {
    return <div className="bg-ink py-32 text-center text-bone/60">Loading…</div>;
  }
  if (!data?.post) throw notFound();

  const post = data.post;
  return (
    <article className="bg-ink pb-32 pt-32 text-bone">
      <div className="mx-auto max-w-3xl px-gutter">
        <Link to="/blog" className="text-xs uppercase tracking-[0.2em] text-bone/70 hover:text-ember">
          ← Back to blog
        </Link>
        {post.tags?.length ? (
          <div className="mt-8 flex flex-wrap gap-1.5">
            {post.tags.map((t: string, i: number) => (
              <span
                key={`${t}-${i}`}
                className="rounded-full border border-bone/15 px-2.5 py-0.5 text-[10px] uppercase tracking-[0.15em] text-bone/60"
              >
                {t}
              </span>
            ))}
          </div>
        ) : null}
        <h1 className="h-display mt-4 text-5xl leading-tight md:text-6xl">{post.title}</h1>
        {post.published_at && (
          <p className="mt-4 text-sm text-bone/70">
            {new Date(post.published_at).toLocaleDateString(undefined, {
              year: "numeric",
              month: "long",
              day: "numeric",
            })}
          </p>
        )}
        {post.cover_image_url && (
          <img
            src={post.cover_image_url}
            alt={post.title}
            className="mt-10 aspect-[16/9] w-full rounded-2xl object-cover"
          />
        )}
        {post.excerpt && (
          <p className="mt-10 text-xl leading-relaxed text-bone/80">{post.excerpt}</p>
        )}
        <div
          className="blog-content prose prose-invert mt-10 max-w-none prose-headings:font-display prose-headings:text-bone prose-a:text-ember prose-strong:text-bone prose-blockquote:border-ember prose-blockquote:text-bone/80"
          dangerouslySetInnerHTML={{ __html: post.content || "" }}
        />
      </div>
    </article>
  );
}
